// TrackFiji V3.3.1 — top-bar "who's online" button and list (people in your own company).
// V3.9.3 — team chat at the bottom of the menu: live messages, @mentions of company users, red bubble on the button
// for unread mentions (small dot for other unread messages). Own messages can be deleted; owners/admins can delete any.
import { FormEvent, KeyboardEvent, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { presenceOf, useCompanyPresence, type Person, type PresenceState } from "../lib/presence";
import { mentionQuery, useTeamChat, type TeamMsg } from "../lib/teamChat";
import { TZ } from "../lib/format";

const LABEL: Record<PresenceState, string> = { online: "Online", away: "Away", offline: "Offline" };
const PAGE: Record<string, string> = { map: "on the map", reports: "in Reports", fleet: "in Fleet", company: "on the Company page", admin: "in Admin" };
const ORDER: Record<PresenceState, number> = { online: 0, away: 1, offline: 2 };

const nameOf = (p: Pick<Person, "full_name" | "email">) => (p.full_name?.trim() || (p.email ?? "").split("@")[0] || "Someone");
const initials = (p: Person) => (p.full_name || p.email || "?").split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((s) => s[0]!.toUpperCase()).join("");
const ago = (iso: string | null, now: number) => {
  if (!iso) return "a while ago";
  const m = (now - Date.parse(iso)) / 60000;
  return m < 1 ? "just now" : m < 60 ? `${Math.round(m)} min ago` : m < 48 * 60 ? `${Math.round(m / 60)} h ago` : `${Math.round(m / 1440)} days ago`;
};
const dayKey = (iso: string) => new Intl.DateTimeFormat("en-CA", { timeZone: TZ }).format(new Date(iso));
const clock = (iso: string) => new Intl.DateTimeFormat("en-GB", { timeZone: TZ, hour: "2-digit", minute: "2-digit" }).format(new Date(iso));
const dayLabel = (iso: string) => {
  const k = dayKey(iso), today = dayKey(new Date().toISOString()), yest = dayKey(new Date(Date.now() - 864e5).toISOString());
  return k === today ? "Today" : k === yest ? "Yesterday" : new Intl.DateTimeFormat("en-GB", { timeZone: TZ, weekday: "short", day: "numeric", month: "short" }).format(new Date(iso));
};

export default function TeamPresence() {
  const { people, now } = useCompanyPresence();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: globalThis.KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", close); document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", esc); };
  }, [open]);

  const rows = useMemo(() => (people ?? [])
    .map((p) => ({ p, s: p.is_me ? ("online" as PresenceState) : presenceOf(p, now) }))
    .sort((a, b) => Number(b.p.is_me) - Number(a.p.is_me) || ORDER[a.s] - ORDER[b.s] || nameOf(a.p).localeCompare(nameOf(b.p))),
  [people, now]);
  const me = rows.find((r) => r.p.is_me)?.p ?? null;
  const chat = useTeamChat(me?.org_id ?? null, open);
  const [insert, setInsert] = useState<{ p: Person; n: number } | null>(null);
  const others = rows.filter((r) => !r.p.is_me);
  const online = others.filter((r) => r.s === "online");
  const here = others.filter((r) => r.s !== "offline");
  if (!people || !others.length) return null;           // nobody else in the company

  const { mentions, messages } = chat.unread;
  return (
    <div className="team" ref={ref}>
      <button type="button" className="team-btn" aria-expanded={open} onClick={() => setOpen(!open)}
        title={`${online.length} colleague${online.length === 1 ? "" : "s"} online${mentions ? ` · ${mentions} new mention${mentions === 1 ? "" : "s"}` : messages ? ` · ${messages} new message${messages === 1 ? "" : "s"}` : ""}`}>
        <span className="team-faces" aria-hidden="true">
          {here.slice(0, 3).map(({ p, s }) => <i key={p.user_id} className={`face ${s}`}>{initials(p)}</i>)}
          {!here.length && <i className="face none">–</i>}
        </span>
        <span className="team-count">{online.length} online</span>
        {mentions > 0 ? <span className="team-badge" aria-label={`${mentions} new mention${mentions === 1 ? "" : "s"}`}>{mentions > 9 ? "9+" : mentions}</span>
          : messages > 0 ? <span className="team-dot" aria-label="New team messages" /> : null}
      </button>
      {open && (
        <div className="team-menu" role="dialog" aria-label="Your team">
          <header><strong>Your team</strong><span className="sub">{online.length} online · {here.length - online.length} away</span></header>
          <ul className="team-people">
            {rows.map(({ p, s }) => (
              <li key={p.user_id}>
                <i className={`face ${s}`}>{initials(p)}</i>
                <span className="who">
                  <b>{nameOf(p)}{p.is_me && <span className="sub"> (you)</span>}</b>
                  <span className="sub">
                    {s === "online" ? (p.is_me ? "Online" : `Online ${PAGE[p.page ?? ""] ?? ""}`.trim())
                      : s === "away" ? `Away · page open, last active ${ago(p.last_active, now)}`
                      : p.last_active || p.last_seen ? `Offline · last active ${ago(p.last_active ?? p.last_seen, now)}` : "Hasn't signed in yet"}
                  </span>
                </span>
                {!p.is_me && <button type="button" className="tc-at" title={`Mention ${nameOf(p)} in the chat`} aria-label={`Mention ${nameOf(p)}`}
                  onClick={() => setInsert({ p, n: Date.now() })}>@</button>}
                <span className={`pres-dot ${s}`} title={LABEL[s]} />
              </li>
            ))}
          </ul>
          {me && <TeamChat people={rows.map((r) => r.p)} me={me} chat={chat} insert={insert} />}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- chat
function TeamChat({ people, me, chat, insert }: {
  people: Person[]; me: Person; chat: ReturnType<typeof useTeamChat>; insert: { p: Person; n: number } | null;
}) {
  const [text, setText] = useState("");
  const [picked, setPicked] = useState<Map<string, string>>(new Map());   // user id → "@Name" inserted
  const [mq, setMq] = useState<{ start: number; q: string } | null>(null);
  const [hi, setHi] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const ta = useRef<HTMLTextAreaElement>(null);
  const byId = useMemo(() => new Map(people.map((p) => [p.user_id, p])), [people]);
  const canModerate = me.role === "org_owner" || me.role === "skyward_admin";

  // stay at the bottom as messages arrive
  const count = chat.msgs?.length ?? 0;
  useEffect(() => { const el = box.current; if (el) el.scrollTop = el.scrollHeight; }, [count]);
  // "@" button in the people list
  useEffect(() => { if (insert) addMention(insert.p, null); }, [insert?.n]); // eslint-disable-line

  const suggestions = useMemo(() => {
    if (!mq) return [];
    const q = mq.q.toLowerCase();
    return people.filter((p) => !p.is_me && (nameOf(p).toLowerCase().split(/\s+/).some((w) => w.startsWith(q)) || nameOf(p).toLowerCase().startsWith(q)
      || (p.email ?? "").toLowerCase().startsWith(q))).slice(0, 6);
  }, [mq, people]);

  function addMention(p: Person, at: { start: number; q: string } | null) {
    const el = ta.current, tag = `@${nameOf(p)}`;
    const caret = el?.selectionStart ?? text.length;
    const start = at ? at.start : caret, end = at ? at.start + 1 + at.q.length : caret;
    const pre = text.slice(0, start), post = text.slice(end);
    const lead = !at && pre && !/\s$/.test(pre) ? " " : "";
    const next = `${pre}${lead}${tag} ${post.replace(/^\s+/, "")}`;
    setText(next);
    setPicked((m) => new Map(m).set(p.user_id, tag));
    setMq(null);
    requestAnimationFrame(() => { const c = (pre + lead + tag + " ").length; el?.focus(); el?.setSelectionRange(c, c); });
  }

  function onChange(v: string, caret: number) {
    setText(v); setErr(null);
    const q = mentionQuery(v, caret);
    setMq(q); setHi(0);
  }

  async function send(e?: FormEvent) {
    e?.preventDefault();
    const body = text.trim();
    if (!body || busy) return;
    const ids = [...picked].filter(([, tag]) => body.includes(tag)).map(([id]) => id);
    setBusy(true); setErr(null);
    try { await chat.send(body, ids); setText(""); setPicked(new Map()); setMq(null); }
    catch (x: any) { setErr(x.message); } finally { setBusy(false); ta.current?.focus(); }
  }

  function onKey(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (mq && suggestions.length) {
      if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => (h + 1) % suggestions.length); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => (h - 1 + suggestions.length) % suggestions.length); return; }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); addMention(suggestions[hi], mq); return; }
      if (e.key === "Escape") { e.stopPropagation(); setMq(null); return; }
    }
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  }

  async function del(m: TeamMsg) {
    if (!confirm("Delete this message for everyone?")) return;
    try { await chat.remove(m.id); } catch (x: any) { setErr(x.message); }
  }

  let lastDay = "";
  return (
    <section className="tc" aria-label="Team chat">
      <div className="tc-head"><strong>Team chat</strong><span className="sub">Only people in your company can see this. Kept 90 days.</span></div>
      <div className="tc-list" ref={box} aria-live="polite">
        {chat.msgs === null && !chat.err && <p className="sub tc-empty">Loading…</p>}
        {chat.err && <p className="error tc-empty">{chat.err}</p>}
        {chat.msgs?.length === 0 && <p className="sub tc-empty">No messages yet. Say hello, or leave a note for the team — type @ to mention someone.</p>}
        {chat.msgs?.map((m) => {
          const d = dayKey(m.created_at); const sep = d !== lastDay ? (lastDay = d, true) : false;
          const who = m.user_id ? byId.get(m.user_id) : undefined;
          const mine = m.user_id === me.user_id;
          const forMe = !m.deleted_at && m.mentions.includes(me.user_id);
          return (
            <div key={m.id}>
              {sep && <div className="tc-day"><span>{dayLabel(m.created_at)}</span></div>}
              <div className={`tc-msg ${mine ? "mine" : ""} ${forMe ? "for-me" : ""} ${m.deleted_at ? "deleted" : ""}`}>
                <div className="tc-meta">
                  <b>{who ? nameOf(who) : "Former member"}</b><span className="sub">{clock(m.created_at)}</span>
                  {!m.deleted_at && (mine || canModerate) && <button type="button" className="tc-del" onClick={() => del(m)} title="Delete message" aria-label="Delete message">×</button>}
                </div>
                <div className="tc-body">{m.deleted_at ? <i>Message deleted</i> : renderBody(m, byId, me.user_id)}</div>
              </div>
            </div>
          );
        })}
      </div>
      <form className="tc-form" onSubmit={send}>
        {mq && suggestions.length > 0 && (
          <ul className="tc-suggest" role="listbox" aria-label="Mention someone">
            {suggestions.map((p, i) => (
              <li key={p.user_id} role="option" aria-selected={i === hi} className={i === hi ? "on" : ""}
                onMouseDown={(e) => { e.preventDefault(); addMention(p, mq); }}>
                <b>{nameOf(p)}</b><span className="sub">{p.email}</span>
              </li>
            ))}
          </ul>
        )}
        <textarea ref={ta} rows={2} maxLength={2000} value={text} placeholder="Message your team… (@ to mention, Enter to send)"
          aria-label="Message your team" onChange={(e) => onChange(e.target.value, e.target.selectionStart)} onKeyDown={onKey}
          onClick={(e) => setMq(mentionQuery(text, (e.target as HTMLTextAreaElement).selectionStart))} />
        <button className="btn-primary small" disabled={busy || !text.trim()}>{busy ? "…" : "Send"}</button>
      </form>
      {err && <div className="error tc-err">{err}</div>}
    </section>
  );
}

/** Message text with @mentions highlighted (yours stronger). Plain text only — no HTML from messages is ever rendered. */
function renderBody(m: TeamMsg, byId: Map<string, Person>, myId: string): ReactNode {
  const tags = m.mentions.map((id) => ({ id, tag: byId.get(id) ? `@${nameOf(byId.get(id)!)}` : null }))
    .filter((t): t is { id: string; tag: string } => !!t.tag).sort((a, b) => b.tag.length - a.tag.length);
  if (!tags.length) return m.body;
  const re = new RegExp(`(${tags.map((t) => t.tag.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`, "g");
  return m.body.split(re).map((part, i) => {
    const t = tags.find((x) => x.tag === part);
    return t ? <span key={i} className={`tc-mention ${t.id === myId ? "me" : ""}`}>{part}</span> : part;
  });
}
